import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MagicLinkVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const hasVerified = useRef(false); // Prevent double-execution in StrictMode

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('Invalid link - no token provided');
      return;
    }

    // Prevent duplicate verification in React StrictMode
    if (hasVerified.current) {
      return;
    }
    hasVerified.current = true;

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      // Use XMLHttpRequest to avoid issues with fetch body stream being consumed by platform scripts
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API}/auth/magic-link/verify?token=${token}`);
        xhr.setRequestHeader('Accept', 'application/json');
        
        xhr.onreadystatechange = () => {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ success: true, data: response });
              } else {
                resolve({ success: false, error: response.detail || 'Verification failed' });
              }
            } catch (e) {
              resolve({ success: false, error: 'Invalid response from server' });
            }
          }
        };
        
        xhr.onerror = () => {
          resolve({ success: false, error: 'Network error - please try again' });
        };
        
        xhr.send();
      });
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const data = result.data;
      
      if (!data.session_token) {
        throw new Error('No session token received');
      }
      
      // Check remember me preference (default to true if not set)
      const rememberMe = sessionStorage.getItem('remember_me') !== 'false';
      sessionStorage.removeItem('remember_me'); // Clean up
      
      // Store session token based on user preference
      if (rememberMe) {
        localStorage.setItem('session_token', data.session_token);
      } else {
        sessionStorage.setItem('session_token', data.session_token);
      }
      
      // Store device token for trusted device login (bypasses magic link next time)
      if (data.device_token && data.email) {
        try {
          const deviceTokens = JSON.parse(localStorage.getItem('device_tokens') || '{}');
          deviceTokens[data.email.toLowerCase()] = data.device_token;
          localStorage.setItem('device_tokens', JSON.stringify(deviceTokens));
        } catch (e) {
          console.log('Failed to store device token', e);
        }
      }
      
      setStatus('success');
      
      // Redirect to dashboard after a brief moment
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);
      
    } catch (err) {
      console.error('Magic link verification error:', err);
      setStatus('error');
      setError(err.message || 'Failed to verify login link');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <img
          src="https://customer-assets.emergentagent.com/job_agent-earnings-5/artifacts/95ok324x_KB%20-%20Final%20Logo%20%28Updated%29-01.png"
          alt="KILO BEATERS"
          className="h-16 mx-auto mb-8"
        />

        {status === 'verifying' && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto" />
            <h2 className="text-xl font-semibold text-gray-800">Verifying your login...</h2>
            <p className="text-gray-500">Please wait a moment</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">You're logged in!</h2>
            <p className="text-gray-500">Redirecting to your dashboard...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Login Failed</h2>
            <p className="text-gray-500">{error}</p>
            <Button
              onClick={() => navigate('/login')}
              className="mt-4 bg-orange-500 hover:bg-orange-600"
            >
              Back to Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
